import { Component, FunctionComponent } from "react";
import { RouteComponentProps } from "react-router-dom";
import { GetShopReferralsInfoInput, GetShopReferralsInfoOutput, RegisterAccountInput, RegisterAccountOutput, SendReferral, SendReferralInput, SendReferralOutput, ShopReferral } from "../../client/core";
import { Alert, Card, Col, Form, ListGroup, Row, Spinner, Table } from "react-bootstrap";
import { Color, Path, Storage } from "../../env";
import { Auth } from "aws-amplify";
import { Button, icon, variant } from "../form/Button";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";

export interface ReferralDashboardState {
    isLoading: boolean;
    error: string;
    referrals: ShopReferral[]
    status: string;
    shopName: string;
    shopLogo: string;
    activity: Activity;
}

export interface ReferralDashboardProps extends RouteComponentProps {
    GetShopReferralInfo: (input: GetShopReferralsInfoInput) => Promise<GetShopReferralsInfoOutput | Error>
    RegisterAccount(input: RegisterAccountInput): Promise<RegisterAccountOutput>;
    auth: typeof Auth;
}

export enum Activity {
    None,
    SendEmail
}

export default class ReferralDashboard extends Component<
    ReferralDashboardProps,
    ReferralDashboardState
> {
    constructor(props: ReferralDashboardProps) {
        super(props)
        this.state = {
            isLoading: true,
            error: "",
            referrals: [],
            status: "",
            shopName: "",
            shopLogo: "",
            activity: Activity.None
        }
    }

    componentDidMount(): void {
        const shopID = localStorage.getItem(Storage.ShopID)
        if (!shopID) {
            return
        }

        this.props.auth.currentSession().then(session => {
            this.props.GetShopReferralInfo({
                identityToken: session.getIdToken().getJwtToken(),
                shopId: shopID
            }).then(response => {
                if (response instanceof Error) {
                    return this.setState({
                        isLoading: false,
                        error: "Sorry, we were unable to access your referrals at this time. If this issue persists please contact support@stumbled.online."
                    })
                }

                this.setState({
                    isLoading: false,
                    status: response.body?.Status || "",
                    referrals: response.body?.Referrals || [],
                    shopName: response.body?.ShopName || "",
                    shopLogo: response.body?.ShopLogo || ""
                })
            })
        })
    }

    handleCreateFinancialConnection = (): void => {
        this.setState({
            isLoading: true
        })

        const shopID = localStorage.getItem(Storage.ShopID)
        if (!shopID) {
            return this.setState({
                isLoading: false,
                error: "Sorry, we were unable to process this request at this time. If this issue persists please contact support@stumbled.online."
            })
        }

        this.props.auth.currentSession().then(session => {
            this.props.RegisterAccount({
                identityToken: session.getIdToken().getJwtToken(),
                shopID: shopID,
                refreshURL: `${window.location.protocol}//${window.location.host}${Path.Referrals}`,
                returnURL: `${window.location.protocol}//${window.location.host}${Path.Referrals}`,
            }).then(async response => {
                if (response.statusCode !== 200) {
                    return this.setState({
                        isLoading: false,
                        error: "Sorry, we were unable to process this request at this time. If this issue persists please contact support@stumbled.online."
                    })
                }

                this.setState({
                    isLoading: false,
                })

                return window.location.replace(response.accountLink);
            }).catch(err => {
                return this.setState({
                    isLoading: false,
                    error: "Sorry, we were unable to process this request at this time. If this issue persists please contact support@stumbled.online."
                })
            })
        }).catch(err => {
            return this.setState({
                isLoading: false,
                error: "Sorry, we were unable to process this request at this time. If this issue persists please contact support@stumbled.online."
            })
        })
    }

    render() {
        if (this.state.isLoading) {
            return <><h1 style={{ marginBottom: "2rem" }}>Referrals</h1><div style={{ textAlign: "center" }}><Spinner variant="success" animation="border" color={Color.Primary} /></div></>
        }

        if (this.state.status == "Undefined") {
            return <CreateFinancialConnection
                error={this.state.error}
                onSubmit={this.handleCreateFinancialConnection} />
        }

        if (this.state.activity == Activity.SendEmail) {
            return <SendEmail
                sendReferral={new SendReferral().SendReferral}
                auth={this.props.auth}
                shopId={localStorage.getItem(Storage.ShopID) || ""}
                shopName={this.state.shopName}
                shopLogo={this.state.shopLogo}
                onActivityChange={activity => this.setState({ activity })}
            />
        }

        return <ReferralDashboardView
            error={this.state.error}
            referrals={this.state.referrals}
            isConnected={this.state.status == "Complete"}
            onAccountSubmit={this.handleCreateFinancialConnection}
            shopId={localStorage.getItem(Storage.ShopID) || ""}
            onActivityChange={activity => this.setState({ activity: activity })}
        />
    }
}

export interface CreateFinancialConnectionProps {
    error: string
    onSubmit: () => void
}


export const CreateFinancialConnection: FunctionComponent<CreateFinancialConnectionProps> = (props) => (
    <Row>
        <h1 style={{ marginBottom: "2rem" }}>Referrals</h1>
        <Alert variant={"danger"} show={props.error != ""}>
            {props.error}
        </Alert>
        <Card>
            <Card.Body>
                <Card.Title>Join Our Shop Referral Program & Earn 20% Monthly!</Card.Title>
                <Card.Text>Earn 20% of the platform revenue from every shop you refer to Stumbled. When a
                    shop signs up using your referral link, you'll receive a monthly payout based on
                    their earnings—no limits, no hassle!</Card.Text>
                <Card.Text>To receive payments, you'll need to securely connect your bank account using our
                    trusted payment partner, Stripe. This ensures seamless and automated monthly payouts.</Card.Text>
                <Card.Text>How it Works:</Card.Text>
                <ol>
                    <li>Sign up for the referral program.</li>
                    <li>Share your unique referral link.</li>
                    <li>When a shop signs up and earns revenue, you get 20% of our platform fee every month.</li>
                    <li>Payments are sent automatically to your bank account.</li>
                </ol>
                <Button buttonvariant={variant.Primary} onClick={() => props.onSubmit()} name={"Connect Bank"} />
            </Card.Body>
        </Card>
    </Row>
)

export interface ReferralDashboardViewProps {
    error: string
    referrals: ShopReferral[]
    isConnected: boolean
    shopId: string
    onAccountSubmit: () => void
    onActivityChange: (activity: Activity) => void
}

export const ReferralDashboardView: FunctionComponent<ReferralDashboardViewProps> = (props) => (
    <>
        <h1 style={{ marginBottom: "2rem" }}>Referrals</h1>
        <div>
            {(!props.isConnected) &&
                <Card style={{ marginBottom: "1rem", borderColor: Color.Orange }}>
                    <Card.Body>
                        <Card.Title>Payments Account Pending</Card.Title>
                        <Card.Text>Don't worry, you can still refer shops in the meantime. We're just waiting on Stripe to confirm your details so we can make payments into your account.</Card.Text>
                        <Button buttonvariant={variant.Primary} onClick={() => props.onAccountSubmit()} name={"Check Status"} />
                    </Card.Body>
                </Card>
            }
            <Row>
                <Col xs={12} sm={10}>
                    <ListGroup as="ol" style={{ padding: 0, marginBottom: "2rem", borderTop: `1px solid ${Color.Grey}`, borderRadius: 0 }}>
                        <ListGroup.Item
                            as="li"
                            className="d-flex justify-content-between align-items-start"
                            style={{ border: 0, padding: 0, paddingTop: "1rem" }}
                        >
                            <div className="ms-2 me-auto">
                                <div className="fw-bold">Referral Link</div>
                                <p>https://dashboard.{process.env.REACT_APP_BASE_DOMAIN}/referral/{props.shopId}</p>
                            </div>
                            <a onClick={() => navigator.clipboard.writeText(`https://dashboard.${process.env.REACT_APP_BASE_DOMAIN}/referral/${props.shopId}`)} style={{ color: Color.Primary, cursor: "pointer" }}>Copy</a>
                        </ListGroup.Item>
                    </ListGroup>
                </Col>
                <Col xs={12} sm={2}>
                    <ListGroup as="ol" style={{ padding: 0, marginBottom: "2rem", width: "100%", borderTop: `1px solid ${Color.Grey}`, borderRadius: 0 }}>
                        <ListGroup.Item
                            as="li"
                            className="d-flex justify-content-between align-items-start"
                            style={{ border: 0, padding: 0, paddingTop: "1rem", textAlign: "right" }}
                        >
                            <Button buttonvariant={variant.Primary} onClick={() => props.onActivityChange(Activity.SendEmail)} name={"Send Email"} />
                        </ListGroup.Item>
                    </ListGroup>
                </Col>
            </Row>
            <Table bordered hover>
                <thead style={{ background: Color.Primary, color: Color.White }}>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Active</th>
                    </tr>
                </thead>
                <tbody>
                    {props.referrals.map(referral => (
                        <tr>
                            <td>{referral.name}</td>
                            <td>{referral.email}</td>
                            <td>{referral.phone}</td>
                            <td style={{ textAlign: "center" }}>{(referral.isActive) ? <FaCheckCircle size={30} color={Color.Primary} /> : <FaExclamationCircle size={30} color={Color.Red} />}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    </>
)

export interface SendEmailState {
    success: string;
    error: string;
    recipientEmail: string
    recipientName: string
    message: string
    referrerName: string
}

export interface SendEmailProps {
    shopLogo: string
    shopName: string
    shopId: string
    auth: typeof Auth;
    sendReferral(input: SendReferralInput): Promise<SendReferralOutput | Error>
    onActivityChange: (activity: Activity) => void
}

export class SendEmail extends Component<
    SendEmailProps,
    SendEmailState
> {
    constructor(props: SendEmailProps) {
        super(props)
        this.state = {
            success: "",
            recipientEmail: "",
            message: "",
            recipientName: "",
            referrerName: "",
            error: ""
        }
    }

    sendEmail = (): void => {
        if (this.state.recipientEmail == "") {
            return this.setState({
                error: "A recipient email is required but not present"
            })
        }

        if (this.state.recipientName == "") {
            return this.setState({
                error: "A recipient name is required but not present"
            })
        }

        if (this.state.referrerName == "") {
            return this.setState({
                error: "Your name is required but not present"
            })
        }

        this.props.auth.currentSession().then(session => {
            this.props.sendReferral({
                identityToken: session.getIdToken().getJwtToken(),
                message: this.state.message,
                recipientName: this.state.recipientName,
                referrerName: this.state.referrerName,
                email: this.state.recipientEmail,
                shopId: this.props.shopId,
            }).then(response => {
                if ((response instanceof Error) || response.statusCode !== 200) {
                    return this.setState({
                        error: "Sorry, we were unable to process this request at this time. If this issue persists please contact support@stumbled.online."
                    })
                }

                return this.setState({
                    success: "Message sent successfully",
                    message: "",
                    recipientEmail: "",
                    recipientName: "",
                    referrerName: "",
                    error: ""
                })

            })
        })
    }

    render() {
        return (
            <Row>
                <h1>Referrals</h1>
                <Col>
                    <Button buttonvariant={variant.Primary} icon={icon.LeftArrow} onClick={() => this.props.onActivityChange(Activity.None)} />
                    <Form style={{ padding: "1rem" }}>
                        <Alert variant={"danger"} show={this.state.error != ""}>
                            {this.state.error}
                        </Alert>
                        <Alert variant={"success"} show={this.state.success != ""}>
                            {this.state.success}
                        </Alert>
                        <Form.Group className="mb-3">
                            <Form.Label>To</Form.Label>
                            <Form.Control style={{ marginBottom: ".5rem" }} type="text" value={this.state.recipientEmail} placeholder="Their email" onChange={(e) => this.setState({ recipientEmail: e.target.value })} />
                            <Form.Control type="text" placeholder="Their name" value={this.state.recipientName} onChange={(e) => this.setState({ recipientName: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Message</Form.Label>
                            <Form.Control as="textarea" placeholder="Your message" value={this.state.message} rows={3} onChange={(e) => this.setState({ message: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>From</Form.Label>
                            <Form.Control type="text" placeholder="Your name" value={this.state.referrerName} onChange={(e) => this.setState({ referrerName: e.target.value })} />
                        </Form.Group>
                        <Button buttonvariant={variant.Primary} onClick={() => this.sendEmail()} name={"Send Email"} />
                    </Form>
                </Col>
                <Col md={7}><EmailTemplate
                    message={this.state.message}
                    recipientName={this.state.recipientName}
                    referrerName={this.state.referrerName}
                    shopId={this.props.shopId}
                    shopLogo={this.props.shopLogo}
                    shopName={this.props.shopName}
                /></Col>
            </Row>
        )
    }
}

export interface EmailTemplateProps {
    shopLogo: string
    shopName: string
    recipientName: string
    message: string
    shopId: string
    referrerName: string
}

export const EmailTemplate: FunctionComponent<EmailTemplateProps> = (props) => (
    <Card style={{ marginBottom: "4rem" }}>
        <Card.Body>
            <div className={"brand-background"}>
                <div style={{ margin: 0, padding: 0, color: "#000" }}>
                    <table
                        style={{
                            borderCollapse: "collapse",
                            width: "100%",
                        }}
                    >
                        <tbody>
                            <tr>
                                <td>
                                    <div style={{ padding: "0px" }}>
                                        <div
                                            style={{
                                                margin: "0 auto",
                                                maxWidth: "600px",
                                                backgroundColor: "transparent",
                                                textAlign: "center",
                                            }}
                                        >
                                            <h1 style={{ color: "#fff", margin: "1rem" }}>{props.shopName}</h1>
                                            <img
                                                src={props.shopLogo}
                                                alt="Shop Logo"
                                                width="100px"
                                            />
                                            <h2 style={{ color: "#ffffff", margin: "2rem" }}>
                                                Have invited <span style={{ textDecoration: "underline" }}>YOU</span> to join the Stumbled network for local independents
                                            </h2>
                                        </div>

                                        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                                            <img
                                                src="https://s3.eu-west-2.amazonaws.com/stumbled.online/demo.png"
                                                alt="Demo"
                                                style={{ width: "100%", border: "none" }}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: "#ffffff", padding: "60px 30px 10px" }}>
                                        <p><strong>Welcome {props.recipientName || "(Their name)"}</strong>,</p>
                                        <p>{props.message}</p>
                                        <p>Let’s support local together! Join us in helping independent shops thrive and keep our community strong. Get started today:</p>
                                        <p style={{ textAlign: "center" }}>
                                            <a
                                                href={`https://dashboard.stumbled.online/referral?referrer=${props.shopId}`}
                                                style={{ fontWeight: 600, color: "#7681ff" }}
                                            >
                                                https://dashboard.stumbled.online/referral?referrer={props.shopId}
                                            </a>
                                        </p>
                                        <p>
                                            For more information about Stumbled you can visit: <a href="https://stumbled.online" style={{ fontWeight: 600, color: "#7681ff" }} target="_blank" rel="noopener noreferrer">https://stumbled.online</a>.
                                        </p>
                                        <p style={{ color: "#7681ff", fontWeight: 600 }}>{props.referrerName || "(Your name)"}</p>
                                        <p style={{ lineHeight: 0 }}>{props.shopName}</p>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Card.Body>
    </Card>
)